import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Popover } from 'react-tiny-popover';

import Icon from '../../misc/Icon';
import DecoratorIconOptions from '../../../constants/DecoratorIconOptions';
import FieldModes from '../../../constants/FieldModes';
import ThemeContext from '../../../theme/ThemeContext';
import ThemeIcons from '../../../theme/Icons';
import DecoratorConfigContext from '../../DecoratorConfigContext';

// ===================================================================
// Styled Components
// ===================================================================

const StyledTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  background: none;
  border: none;
  padding: 3px 4px;
  border-radius: 3px;
  opacity: 0.55;
  transition: opacity 0.15s ease-in-out, background-color 0.15s ease-in-out;
  vertical-align: middle;

  svg {
    margin-right: 0 !important;
  }

  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.06);
  }

  &:focus {
    outline: none;
  }
`;

const StyledMenu = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.$bg};
  border-radius: ${(props) => props.$radius}px;
  border: 1px solid ${(props) => props.$border};
  box-shadow: 0px 4px 20px rgba(20, 20, 23, 0.18);
  min-width: 200px;
  max-width: 280px;
  overflow: hidden;
  font-family: ${(props) => props.$fontFamily};
`;

const StyledDecoratorRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: background-color 0.1s ease-in-out;

  &:hover {
    background-color: ${(props) => (props.$clickable ? props.$hover : 'transparent')};
  }

  & + & {
    border-top: 1px solid ${(props) => props.$border};
  }
`;

const StyledDecoratorWrapper = styled.span`
  display: inline-flex;
  align-items: center;

  margin-left: auto;

  min-width: 0;
  max-width: 100%;

  flex-shrink: 1;
`;

const StyledInlineGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  white-space: nowrap;

  overflow-x: auto;
  overflow-y: hidden;

  max-width: 240px;
  min-width: 0;

  /* Important */
  position: relative;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;
const StyledSingleDecorator = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid ${(props) => props.$color};
  background: none;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.$color};
  max-width: 100px;
  min-width: 0;
  flex-shrink: 0;
  line-height: 1.4;
  transition: background-color 0.15s ease-in-out;

  svg {
    margin-right: 0 !important;
  }

  &[data-icon-only='true'] {
    padding: 2px;
    gap: 0;
    line-height: 1;
  }

  &:hover {
    background-color: ${(props) => (props.$clickable ? `${props.$color}1a` : 'transparent')};
  }

  &:focus {
    outline: none;
  }
`;

const StyledSingleDecoratorLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const StyledIconWrapper = styled.div`
  flex-shrink: 0;
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 13px;
  }
`;

const StyledRowText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  gap: 2px;
`;

const StyledRowLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.$color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const StyledRowAction = styled.span`
  font-size: 11px;
  color: ${(props) => props.$color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

// ===================================================================
// Helpers
// ===================================================================

/**
 * Whether FieldDecorators draws anything. The text and multiline headers drop their 4px gap
 * when nothing is drawn, so this has to stay the same test as the early return below.
 */
export const hasVisibleDecorators = (decorators, fieldMode) => (
  fieldMode !== FieldModes.pdf
  && Array.isArray(decorators)
  && decorators.some((d) => d.icon || d.label)
);

const getIconDefinition = (iconValue) => {
  if (!iconValue) return null;
  const match = DecoratorIconOptions.find((opt) => opt.value === iconValue);
  return match ? match.icon : ThemeIcons.decorator.image;
};

// ===================================================================
// Component
// ===================================================================

const FieldDecorators = ({
  decorators,
  fieldMode,
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  onDecoratorClick,
}) => {

  const [isOpen, setIsOpen] = useState(false);

  //Read before any early return so every render calls the same hooks in the same order
  const theme = useContext(ThemeContext);
  const { visibleLimitInFields } = useContext(DecoratorConfigContext);

  if (!hasVisibleDecorators(decorators, fieldMode)) return null;

  const validDecorators = decorators.filter((d) => d.icon || d.label);

  const isClickable = fieldMode === FieldModes.fill || fieldMode === FieldModes.readonly;

  const handleDecoratorClick = (decorator) => {
    if (!isClickable) return;
    setIsOpen(false);
    if (onDecoratorClick) {
      onDecoratorClick(fieldId, fieldIdentifier, fieldPositionId, decorator.action);
    }
  };

  if (validDecorators.length <= visibleLimitInFields) {
    return (
      <StyledDecoratorWrapper>
        <StyledInlineGroup ref={(el) => {
          if (el && !el.__wheelBound) { el.__wheelBound = true; el.addEventListener('wheel', function(e) { if (el.scrollWidth > el.clientWidth) { e.preventDefault(); el.scrollLeft += e.deltaY || e.deltaX; } }, {passive: false}); }
        }}>
          {validDecorators.map((decorator, index) => {
            const iconDef = getIconDefinition(decorator.icon);
            const color = decorator.color || theme.primaryColor;
            const displayLabel = decorator.label || '';

            return (
              <StyledSingleDecorator
                key={decorator._id || index}
                type="button"
                $color={color}
                $clickable={isClickable}
                data-icon-only={!displayLabel ? 'true' : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDecoratorClick(decorator);
                }}
                data-testid={`decorator-single-${fieldPositionId}-${index}`}
              >
                {iconDef ? <Icon icon={iconDef} color={color} /> : null}
                {displayLabel ? <StyledSingleDecoratorLabel>{displayLabel}</StyledSingleDecoratorLabel> : null}
              </StyledSingleDecorator>
            );
          })}
        </StyledInlineGroup>
      </StyledDecoratorWrapper>
    );
  }

  // === Multiple decorators — layer-icon trigger + popover menu ===
  const menuContent = (
    <StyledMenu
      $bg={theme.backgroundColor}
      $radius={theme.borderRadius}
      $border={theme.borderColor}
      $fontFamily={theme.fontFamily}
    >
      {validDecorators.map((decorator, index) => {
        const iconDef = getIconDefinition(decorator.icon);
        const color = decorator.color || theme.primaryColor;
        const displayLabel = decorator.label || '';

        return (
          <StyledDecoratorRow
            key={decorator._id || index}
            $clickable={isClickable}
            $hover={theme.secondaryColor}
            $border={theme.borderColor}
            onClick={() => handleDecoratorClick(decorator)}
            data-testid={`decorator-menu-item-${decorator.action || index}`}
          >
            {iconDef ? (
              <StyledIconWrapper>
                <Icon icon={iconDef} color={color} />
              </StyledIconWrapper>
            ) : null}

            <StyledRowText>
              {displayLabel ? (
                <StyledRowLabel $color={color}>
                  {displayLabel}
                </StyledRowLabel>
              ) : null}
              {decorator.action ? (
                <StyledRowAction $color={theme.fontColorSecondary}>
                  {decorator.action}
                </StyledRowAction>
              ) : null}
            </StyledRowText>
          </StyledDecoratorRow>
        );
      })}
    </StyledMenu>
  );

  return (
    <StyledDecoratorWrapper>
      <Popover
        isOpen={isOpen}
        positions={['bottom', 'top', 'right', 'left']}
        reposition={true}
        containerStyle={{ zIndex: '2147483647' }}
        onClickOutside={() => setIsOpen(false)}
        content={menuContent}
      >
        <StyledTrigger
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
          data-testid={`decorator-trigger-${fieldPositionId}`}
        >
          <Icon icon={ThemeIcons.misc.decorators}  />
        </StyledTrigger>
      </Popover>
    </StyledDecoratorWrapper>
  );
};

FieldDecorators.propTypes = {
  decorators: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    icon: PropTypes.string,
    label: PropTypes.string,
    color: PropTypes.string,
    action: PropTypes.string,
  })),
  fieldMode: PropTypes.string,
  fieldId: PropTypes.string,
  fieldIdentifier: PropTypes.string,
  fieldPositionId: PropTypes.string,
  onDecoratorClick: PropTypes.func,
};

export default FieldDecorators;
